<template>
  <div class="wrapper">
    <div
      class="counter rating-display"
      :title="`${rating.name} - ${rating.value}`"
      @click.left.prevent="moveState(true)"
      @click.right.prevent="moveState(false)"
      @click.middle.prevent="resetState()"
    >
      <div
        class="number-container"
        :style="counterStyle"
      >
        <span class="number-text">
          {{ rating.value }}
        </span>
      </div>
    </div>
    <PlusMinus
      v-if="showButtons"
      class="plus-minus"
      @increase="moveState(true)"
      @decrease="moveState(false)"
      @reset="resetState()"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, withDefaults } from "vue";

import type {
  CounterServiceIncludingValue,
  CounterValue,
} from "@/stores/ratingService";

import PlusMinus from "@/components/HydrusRating/PlusMinus.vue";

const props = withDefaults(
  defineProps<{
    rating: CounterServiceIncludingValue,
    showButtons: boolean,
  }>(),
  {
    showButtons: true,
  },
);

const emit = defineEmits<{
  (event: "set", newState: CounterValue, oldState: CounterValue): void;
}>();

const neutralState = 0;

const counterStyle = computed(() => {
  const colours = props.rating.colours;
  if (!colours) {
    return {};
  }

  const resolvedColour =
    props.rating.value > neutralState
      ? colours.like ?? colours.null ?? colours.mixed
      : colours.null;

  if (!resolvedColour) {
    return {};
  }

  return {
    backgroundColor: resolvedColour.brush,
    color: resolvedColour.pen,
    borderColor: resolvedColour.pen,
  };
});

function moveState(up: boolean) {
  console.log('moveState', up);
  // pull current value or neutral/default
  let newState = props.rating.value ?? neutralState;
  // increment it as needed
  newState += up ? 1 : -1;
  // make sure that it stays in bounds: x >= neutralState
  newState = Math.max(neutralState, newState);
  // emit the new value (and the old value)
  console.log("CounterRating.moveState().emit", ['set', newState, props.rating.value]);
  emit("set", newState, props.rating.value);
}

function resetState() {
  console.log("CounterRating.resetState().emit", ['set', neutralState, props.rating.value]);
  emit("set", neutralState, props.rating.value);
}
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;

  .rating-display, .plus-minus {
    flex-shrink: 0;
  }
  .rating-display {
    flex-grow: 1;
    display: flex;
    align-content: center;
    justify-content: center;
  }
  .plus-minus {
    flex-grow: 0;
    margin-left: 1em;
  }

  .rating-display.counter {
    display: inline-block;
    padding: 5px 3px;
    background-color: transparent;
    color: var(--text-muted);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: var(--btn-hover-bg);
    }
    .number-container {
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: monospace;
    }
  }
}
</style>
